import { useEffect, useState, useCallback } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    Shield,
    Users,
    Activity,
    Wallet,
    CheckCircle2,
    XCircle,
    AlertTriangle,
    Clock,
    Zap,
    ArrowLeft,
    Mail,
    RefreshCcw,
    Server,
    LogOut,
    UserCheck,
    LogIn,
    Crown,
} from "lucide-react";

import { api, formatCRC, formatDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Header from "../components/sinpe/Header";
import Footer from "../components/sinpe/Footer";

import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";

const MetricCard = ({ icon: Icon, label, value, accent = "bg-[#064E3B]/5 text-[#064E3B]", help }) => (
    <div className="card-clean p-6 flex flex-col gap-3" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-widest text-[#64748B]">{label}</p>
            <div className={`h-10 w-10 rounded-full ${accent} flex items-center justify-center`}>
                <Icon className="w-5 h-5" strokeWidth={1.75} />
            </div>
        </div>
        <p className="font-display text-3xl md:text-4xl font-semibold text-[#022C22] tracking-tight">{value}</p>
        {help && <p className="text-xs text-[#64748B]">{help}</p>}
    </div>
);

const LogStatusBadge = ({ status }) => {
    if (status === "verified") return <span className="badge-verified"><CheckCircle2 className="w-3 h-3" /> verified</span>;
    if (status === "failed") return <span className="badge-failed"><XCircle className="w-3 h-3" /> failed</span>;
    if (status === "api_error")
        return (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FEE2E2] text-[#991B1B] text-xs font-medium" data-testid="log-api-error">
                <AlertTriangle className="w-3 h-3" /> api_error
            </span>
        );
    return <span className="badge-pending"><Clock className="w-3 h-3" /> pending</span>;
};

export default function AdminPortal() {
    const { user, loading, logout, setUser } = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [metrics, setMetrics] = useState(null);
    const [logs, setLogs] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [onlyErrors, setOnlyErrors] = useState(false);
    const [updatingId, setUpdatingId] = useState(null);
    const [impersonatingId, setImpersonatingId] = useState(null);
    const [planUpdatingId, setPlanUpdatingId] = useState(null);

    const loadAll = useCallback(async () => {
        setLoadingData(true);
        try {
            const [u, m, l] = await Promise.all([
                api.get("/admin/users"),
                api.get("/admin/metrics"),
                api.get("/admin/cybersinpe-logs", { params: { limit: 100, only_errors: onlyErrors } }),
            ]);
            setUsers(u.data);
            setMetrics(m.data);
            setLogs(l.data);
        } catch (e) {
            toast.error("Error cargando datos");
        } finally {
            setLoadingData(false);
        }
    }, [onlyErrors]);

    useEffect(() => {
        if (user?.is_admin) loadAll();
    }, [user, loadAll]);

    const toggleAccess = async (target) => {
        setUpdatingId(target.user_id);
        try {
            const { data } = await api.patch(`/admin/users/${target.user_id}/access`, {
                is_active: !target.is_active,
            });
            setUsers((prev) => prev.map((u) => (u.user_id === data.user_id ? { ...u, ...data } : u)));
            toast.success(data.is_active ? `${data.name} activado` : `${data.name} desactivado`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No pudimos actualizar el estado");
        } finally {
            setUpdatingId(null);
        }
    };

    const impersonate = async (target) => {
        if (!window.confirm(`¿Entrar al dashboard como ${target.name}?\n\nVerás todo lo que ve este vendedor por 30 min. Podrás volver al admin con un clic.`)) {
            return;
        }
        setImpersonatingId(target.user_id);
        try {
            const { data } = await api.post(`/admin/users/${target.user_id}/impersonate`);
            toast.success(`Entrando como ${data.target_name}...`);
            // Refresh auth state and navigate to dashboard
            const me = await api.get("/auth/me");
            setUser(me.data);
            navigate("/dashboard", { replace: true });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo impersonar");
            setImpersonatingId(null);
        }
    };

    const setPlan = async (target, nextPlan) => {
        // nextPlan: "basic" | "pro_100" | "pro_1" | "pro_2"
        const currentTier = target.subscription_tier || (target.plan === "pro" ? "pro_legacy" : "basic");
        // Avoid no-op calls (e.g. user selects same value)
        if (
            (nextPlan === "basic" && target.plan === "basic") ||
            (nextPlan === "pro_100" && currentTier === "pro_100") ||
            (nextPlan === "pro_1" && currentTier === "pro_1") ||
            (nextPlan === "pro_2" && currentTier === "pro_2")
        ) {
            return;
        }
        setPlanUpdatingId(target.user_id);
        try {
            const { data } = await api.patch(`/admin/users/${target.user_id}/plan`, { plan: nextPlan });
            setUsers((prev) => prev.map((u) => (u.user_id === data.user_id ? { ...u, ...data } : u)));
            const labelMap = {
                basic: `${data.name} ahora es Gratis`,
                pro_100: `${data.name} ahora es Plan Emprendedor (100 tx/mes)`,
                pro_1: `${data.name} ahora es Pyme Pro · Nivel 1 (300 tx/mes)`,
                pro_2: `${data.name} ahora es Pyme Pro · Nivel 2 (1,200 tx/mes)`,
            };
            toast.success(labelMap[nextPlan] || `Plan actualizado`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo cambiar el plan");
        } finally {
            setPlanUpdatingId(null);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <div className="h-10 w-10 rounded-full border-2 border-[#064E3B] border-t-transparent animate-spin" />
            </div>
        );
    }
    if (!user) return <Navigate to="/" replace />;
    if (!user.is_admin) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6" data-testid="admin-denied">
                <div className="card-clean p-10 max-w-md text-center">
                    <div className="h-14 w-14 mx-auto rounded-full bg-[#FEE2E2] flex items-center justify-center">
                        <Shield className="w-7 h-7 text-[#991B1B]" />
                    </div>
                    <h2 className="font-display text-2xl font-semibold text-[#022C22] mt-5">Acceso restringido</h2>
                    <p className="text-[#334155] mt-2">
                        Este panel es exclusivo para administradores de SINPE-Flow.
                    </p>
                    <Link to="/dashboard" className="btn-secondary mt-6 inline-flex">
                        <ArrowLeft className="w-4 h-4" /> Volver al dashboard
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-portal-page">
            <Header
                rightSlot={
                    <>
                        <Link to="/dashboard" className="btn-ghost flex items-center gap-2" data-testid="back-to-dashboard">
                            <ArrowLeft className="w-4 h-4" /> Mi dashboard
                        </Link>
                        <button onClick={loadAll} className="btn-ghost flex items-center gap-2" data-testid="refresh-button" title="Recargar">
                            <RefreshCcw className={`w-4 h-4 ${loadingData ? "animate-spin" : ""}`} />
                        </button>
                        <button onClick={logout} className="btn-ghost" data-testid="logout-button" title="Cerrar sesión">
                            <LogOut className="w-4 h-4" />
                        </button>
                    </>
                }
            />

            <main className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                >
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#064E3B] text-white text-xs font-medium tracking-wide mb-3" data-testid="admin-badge">
                            <Shield className="w-3.5 h-3.5" /> Admin portal
                        </div>
                        <h1 className="font-display text-4xl md:text-5xl font-semibold text-[#022C22] tracking-tight">
                            Control total de SINPE-Flow.
                        </h1>
                        <p className="text-[#334155] mt-3 text-lg">
                            Gestiona vendedores, métricas y la salud del API de CyberSINPE.
                        </p>
                    </div>
                </motion.div>

                <Tabs defaultValue="users" className="mt-10">
                    <TabsList className="bg-white border border-[#E2E8F0] p-1 rounded-full" data-testid="admin-tabs">
                        <TabsTrigger value="users" className="rounded-full data-[state=active]:bg-[#064E3B] data-[state=active]:text-white px-5" data-testid="tab-users">
                            <Users className="w-4 h-4 mr-2" /> Vendedores
                        </TabsTrigger>
                        <TabsTrigger value="metrics" className="rounded-full data-[state=active]:bg-[#064E3B] data-[state=active]:text-white px-5" data-testid="tab-metrics">
                            <Activity className="w-4 h-4 mr-2" /> Métricas
                        </TabsTrigger>
                        <TabsTrigger value="logs" className="rounded-full data-[state=active]:bg-[#064E3B] data-[state=active]:text-white px-5" data-testid="tab-logs">
                            <Server className="w-4 h-4 mr-2" /> Logs CyberSINPE
                        </TabsTrigger>
                    </TabsList>

                    {/* USERS */}
                    <TabsContent value="users" className="mt-6">
                        <div className="card-clean overflow-hidden" data-testid="users-card">
                            <div className="p-5 md:p-6 border-b border-[#E2E8F0]">
                                <h2 className="font-display text-xl md:text-2xl font-medium text-[#022C22]">Gestión de clientes</h2>
                                <p className="text-sm text-[#64748B] mt-1">
                                    {users.length} {users.length === 1 ? "vendedor registrado" : "vendedores registrados"}. Usa el interruptor para activar o desactivar el acceso.
                                </p>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left border-b border-[#E2E8F0] bg-[#F8FAFC]">
                                            <th className="px-5 md:px-6 py-4 font-medium text-[#64748B] uppercase tracking-wider text-xs">Vendedor</th>
                                            <th className="px-5 md:px-6 py-4 font-medium text-[#64748B] uppercase tracking-wider text-xs">Correo</th>
                                            <th className="px-5 md:px-6 py-4 font-medium text-[#64748B] uppercase tracking-wider text-xs">Registrado</th>
                                            <th className="px-5 md:px-6 py-4 font-medium text-[#64748B] uppercase tracking-wider text-xs text-right" title="Transacciones del ciclo de facturación actual (se reinicia al cambiar de plan o al inicio del mes)">Tx (ciclo)</th>
                                            <th className="px-5 md:px-6 py-4 font-medium text-[#64748B] uppercase tracking-wider text-xs text-right">Verificado</th>
                                            <th className="px-5 md:px-6 py-4 font-medium text-[#64748B] uppercase tracking-wider text-xs text-center">Plan</th>
                                            <th className="px-5 md:px-6 py-4 font-medium text-[#64748B] uppercase tracking-wider text-xs text-right">Acceso</th>
                                            <th className="px-5 md:px-6 py-4 font-medium text-[#64748B] uppercase tracking-wider text-xs text-right">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loadingData && users.length === 0 ? (
                                            <tr><td colSpan={8} className="px-6 py-16 text-center text-[#64748B]" data-testid="users-loading">
                                                <div className="inline-block h-6 w-6 rounded-full border-2 border-[#064E3B] border-t-transparent animate-spin" />
                                            </td></tr>
                                        ) : users.map((u) => (
                                            <tr key={u.user_id} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors" data-testid={`user-row-${u.user_id}`}>
                                                <td className="px-5 md:px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        {u.picture ? (
                                                            <img src={u.picture} alt={u.name} className="w-9 h-9 rounded-full object-cover" />
                                                        ) : (
                                                            <div className="w-9 h-9 rounded-full bg-[#064E3B] flex items-center justify-center text-white text-xs font-medium">
                                                                {u.name?.[0] || "U"}
                                                            </div>
                                                        )}
                                                        <div>
                                                            <p className="font-medium text-[#022C22] flex items-center gap-2">
                                                                {u.business_name || u.name}
                                                                {u.is_admin && (
                                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#064E3B] text-white text-[10px] font-medium" data-testid="admin-pill">
                                                                        <Shield className="w-2.5 h-2.5" /> Admin
                                                                    </span>
                                                                )}
                                                            </p>
                                                            {u.business_name && (
                                                                <p className="text-xs text-[#64748B]">{u.name}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-5 md:px-6 py-4 text-[#334155]">
                                                    <div className="flex items-center gap-1.5">
                                                        <Mail className="w-3.5 h-3.5 text-[#64748B]" /> {u.email}
                                                    </div>
                                                </td>
                                                <td className="px-5 md:px-6 py-4 text-[#334155]">{formatDate(u.created_at)}</td>
                                                <td className="px-5 md:px-6 py-4 text-right font-mono text-[#022C22]">{u.transaction_count}</td>
                                                <td className="px-5 md:px-6 py-4 text-right font-mono font-medium text-[#022C22]">{formatCRC(u.verified_amount)}</td>
                                                <td className="px-5 md:px-6 py-4 text-center">
                                                    {(() => {
                                                        // Selected value in the dropdown:
                                                        //   basic              → "basic"
                                                        //   pro + tier=pro_100 → "pro_100"
                                                        //   pro + tier=pro_1   → "pro_1"
                                                        //   pro + tier=pro_2   → "pro_2"
                                                        //   pro + no tier      → "pro_legacy" (read-only label, admin must explicitly pick a tier)
                                                        let selected;
                                                        if (u.plan !== "pro") {
                                                            selected = "basic";
                                                        } else if (u.subscription_tier === "pro_2") {
                                                            selected = "pro_2";
                                                        } else if (u.subscription_tier === "pro_1") {
                                                            selected = "pro_1";
                                                        } else if (u.subscription_tier === "pro_100") {
                                                            selected = "pro_100";
                                                        } else {
                                                            selected = "pro_legacy";
                                                        }
                                                        return (
                                                            <div className="flex flex-col items-center gap-1.5">
                                                                <Select
                                                                    value={selected}
                                                                    onValueChange={(val) => setPlan(u, val)}
                                                                    disabled={planUpdatingId === u.user_id}
                                                                >
                                                                    <SelectTrigger
                                                                        className="w-[190px] h-8 rounded-full text-xs font-medium bg-white border-[#E2E8F0]"
                                                                        data-testid={`plan-toggle-${u.user_id}`}
                                                                    >
                                                                        <SelectValue />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value="basic" data-testid={`plan-option-basic-${u.user_id}`}>Gratis · 20 tx</SelectItem>
                                                                        <SelectItem value="pro_100" data-testid={`plan-option-pro_100-${u.user_id}`}>Emprendedor (100)</SelectItem>
                                                                        <SelectItem value="pro_1" data-testid={`plan-option-pro_1-${u.user_id}`}>Pyme Pro · Nivel 1 (300)</SelectItem>
                                                                        <SelectItem value="pro_2" data-testid={`plan-option-pro_2-${u.user_id}`}>Pyme Pro · Nivel 2 (1,200)</SelectItem>
                                                                        {selected === "pro_legacy" && (
                                                                            <SelectItem value="pro_legacy" disabled>
                                                                                Pyme Pro (sin nivel)
                                                                            </SelectItem>
                                                                        )}
                                                                    </SelectContent>
                                                                </Select>
                                                                {u.plan === "pro" && u.subscription_expires_at && (
                                                                    <span className="text-[10px] text-[#64748B]" data-testid={`plan-expires-${u.user_id}`}>
                                                                        renueva {formatDate(u.subscription_expires_at)}
                                                                    </span>
                                                                )}
                                                                {planUpdatingId === u.user_id && (
                                                                    <span className="h-3 w-3 rounded-full border-2 border-[#064E3B] border-t-transparent animate-spin" />
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="px-5 md:px-6 py-4 text-right">
                                                    <div className="inline-flex items-center gap-2.5">
                                                        <span className={`text-xs font-medium ${u.is_active ? "text-[#065F46]" : "text-[#991B1B]"}`} data-testid={`access-label-${u.user_id}`}>
                                                            {u.is_active ? "Activo" : "Inactivo"}
                                                        </span>
                                                        <Switch
                                                            checked={u.is_active}
                                                            onCheckedChange={() => toggleAccess(u)}
                                                            disabled={updatingId === u.user_id || u.user_id === user.user_id}
                                                            data-testid={`access-toggle-${u.user_id}`}
                                                        />
                                                    </div>
                                                </td>
                                                <td className="px-5 md:px-6 py-4 text-right">
                                                    <button
                                                        onClick={() => impersonate(u)}
                                                        disabled={
                                                            impersonatingId === u.user_id ||
                                                            u.user_id === user.user_id ||
                                                            !u.is_active
                                                        }
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F8FAFC] border border-[#E2E8F0] text-xs font-medium text-[#022C22] hover:bg-[#064E3B] hover:text-white hover:border-[#064E3B] transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#F8FAFC] disabled:hover:text-[#022C22] disabled:hover:border-[#E2E8F0]"
                                                        data-testid={`impersonate-${u.user_id}`}
                                                        title={
                                                            u.user_id === user.user_id
                                                                ? "No podés impersonarte"
                                                                : !u.is_active
                                                                ? "Vendedor inactivo"
                                                                : `Entrar como ${u.name}`
                                                        }
                                                    >
                                                        {impersonatingId === u.user_id ? (
                                                            <span className="h-3 w-3 rounded-full border-2 border-[#064E3B] border-t-transparent animate-spin" />
                                                        ) : (
                                                            <LogIn className="w-3.5 h-3.5" />
                                                        )}
                                                        Entrar como
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </TabsContent>

                    {/* METRICS */}
                    <TabsContent value="metrics" className="mt-6">
                        {!metrics ? (
                            <div className="card-clean p-16 flex items-center justify-center" data-testid="metrics-loading">
                                <div className="h-8 w-8 rounded-full border-2 border-[#064E3B] border-t-transparent animate-spin" />
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                                    <MetricCard icon={Users} label="Vendedores totales" value={metrics.total_users} accent="bg-[#E0E7FF] text-[#3730A3]" help={`${metrics.active_users} activos`} />
                                    <MetricCard icon={Activity} label="Transacciones" value={metrics.total_transactions} accent="bg-[#064E3B]/5 text-[#064E3B]" />
                                    <MetricCard icon={Wallet} label="Dinero verificado" value={formatCRC(metrics.total_verified_amount)} accent="bg-[#D1FAE5] text-[#065F46]" />
                                    <MetricCard
                                        icon={metrics.api_healthy ? Zap : AlertTriangle}
                                        label="API CyberSINPE"
                                        value={metrics.api_healthy ? "Saludable" : "Degradado"}
                                        accent={metrics.api_healthy ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#FEE2E2] text-[#991B1B]"}
                                        help={`${metrics.cybersinpe_errors_24h} errores / ${metrics.cybersinpe_calls_24h} llamadas (24h)`}
                                    />
                                </div>

                                <div className="mt-6 card-clean p-6 md:p-8">
                                    <h3 className="font-display text-xl font-medium text-[#022C22] mb-5">Desglose de transacciones</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                        <div className="rounded-2xl bg-[#D1FAE5] p-5">
                                            <div className="flex items-center gap-2 text-[#065F46]">
                                                <CheckCircle2 className="w-4 h-4" />
                                                <span className="text-xs uppercase tracking-widest font-medium">Verificadas</span>
                                            </div>
                                            <p className="font-display text-4xl font-semibold text-[#022C22] mt-2">{metrics.verified_transactions}</p>
                                        </div>
                                        <div className="rounded-2xl bg-[#FEF3C7] p-5">
                                            <div className="flex items-center gap-2 text-[#92400E]">
                                                <Clock className="w-4 h-4" />
                                                <span className="text-xs uppercase tracking-widest font-medium">Pendientes</span>
                                            </div>
                                            <p className="font-display text-4xl font-semibold text-[#022C22] mt-2">{metrics.pending_transactions}</p>
                                        </div>
                                        <div className="rounded-2xl bg-[#FEE2E2] p-5">
                                            <div className="flex items-center gap-2 text-[#991B1B]">
                                                <XCircle className="w-4 h-4" />
                                                <span className="text-xs uppercase tracking-widest font-medium">Rechazadas</span>
                                            </div>
                                            <p className="font-display text-4xl font-semibold text-[#022C22] mt-2">{metrics.failed_transactions}</p>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </TabsContent>

                    {/* LOGS */}
                    <TabsContent value="logs" className="mt-6">
                        <div className="card-clean overflow-hidden" data-testid="logs-card">
                            <div className="p-5 md:p-6 border-b border-[#E2E8F0] flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div>
                                    <h2 className="font-display text-xl md:text-2xl font-medium text-[#022C22]">Logs de CyberSINPE</h2>
                                    <p className="text-sm text-[#64748B] mt-1">
                                        Cada llamada al API, su resultado, latencia y errores. Últimas {logs.length}.
                                    </p>
                                </div>
                                <label className="inline-flex items-center gap-2.5 px-3 py-2 rounded-full bg-[#F8FAFC] border border-[#E2E8F0] cursor-pointer">
                                    <Switch
                                        checked={onlyErrors}
                                        onCheckedChange={setOnlyErrors}
                                        data-testid="only-errors-toggle"
                                    />
                                    <span className="text-sm text-[#022C22] font-medium">Solo errores</span>
                                </label>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left border-b border-[#E2E8F0] bg-[#F8FAFC]">
                                            <th className="px-5 md:px-6 py-4 font-medium text-[#64748B] uppercase tracking-wider text-xs">Timestamp</th>
                                            <th className="px-5 md:px-6 py-4 font-medium text-[#64748B] uppercase tracking-wider text-xs">Referencia</th>
                                            <th className="px-5 md:px-6 py-4 font-medium text-[#64748B] uppercase tracking-wider text-xs text-right">Monto</th>
                                            <th className="px-5 md:px-6 py-4 font-medium text-[#64748B] uppercase tracking-wider text-xs">Resultado</th>
                                            <th className="px-5 md:px-6 py-4 font-medium text-[#64748B] uppercase tracking-wider text-xs text-right">Latencia</th>
                                            <th className="px-5 md:px-6 py-4 font-medium text-[#64748B] uppercase tracking-wider text-xs">Error</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {logs.length === 0 ? (
                                            <tr><td colSpan={6} className="px-6 py-20 text-center" data-testid="logs-empty">
                                                <div className="inline-flex flex-col items-center gap-3">
                                                    <div className="h-14 w-14 rounded-full bg-[#F8FAFC] flex items-center justify-center">
                                                        <Server className="w-6 h-6 text-[#64748B]" />
                                                    </div>
                                                    <p className="font-medium text-[#022C22]">Sin llamadas registradas todavía</p>
                                                    <p className="text-sm text-[#64748B] max-w-sm">
                                                        Cuando un cliente envíe un pago al sistema, vas a ver acá cada llamada al API.
                                                    </p>
                                                </div>
                                            </td></tr>
                                        ) : logs.map((l) => (
                                            <tr key={l.log_id} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors" data-testid={`log-row-${l.log_id}`}>
                                                <td className="px-5 md:px-6 py-4 text-[#334155] font-mono text-xs">{formatDate(l.timestamp)}</td>
                                                <td className="px-5 md:px-6 py-4 font-mono text-[#022C22]">{l.reference}</td>
                                                <td className="px-5 md:px-6 py-4 text-right font-mono text-[#022C22]">{formatCRC(l.amount)}</td>
                                                <td className="px-5 md:px-6 py-4"><LogStatusBadge status={l.status} /></td>
                                                <td className="px-5 md:px-6 py-4 text-right font-mono text-[#64748B]">{l.duration_ms}ms</td>
                                                <td className="px-5 md:px-6 py-4 text-[#991B1B] text-xs max-w-xs truncate">{l.error || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>
            </main>

            <Footer />
        </div>
    );
}
